const CONSULTATION_SERVICE_ID = 'service_consultation_request'
const CONSULTATION_PRODUCT_ID = 32

export interface ConsultationDisplay {
    title: string
    context?: string
    comments?: string
}

const CONTEXT_LABELS: Record<string, string> = {
    general: 'Consultation',
    general_consultation: 'Consultation',
    management_assistance: 'Management Assistance',
    maintenance_assistance: 'Maintenance Assistance',
    legal_assistance: 'Legal Assistance',
    finance_assistance: 'Finance Assistance',
    landlord_consultation: 'Landlord Consultation',
    tenant_consultation: 'Tenant Consultation',
    buyer_consultation: 'Buyer Consultation',
    create_unit_assistance: 'Create Unit Assistance',
    finding_tenant_cancellation: 'Finding Tenant Cancellation',
    unit_edit_assistance: 'Unit Edit Assistance',
    finding_tenant_poa_pre_payment_verification: 'POA Verification',
    unit_verification_callback_follow_up: 'Verification Callback Follow-up',
    unit_owner_verification: 'Unit Owner Verification',
}

const asText = (value: unknown): string => typeof value === 'string' ? value.trim() : ''
const normalizeServiceId = (value: unknown): string => asText(value)
    .toLowerCase()
    .replace(/^services\./, '')
    .replace(/[\s-]+/g, '_')
const toArray = <T,>(value: T[] | null | undefined): T[] => Array.isArray(value) ? value : []

const getServiceTokens = (service: any): string[] => [
    service?.serviceId,
    service?.ServiceId,
    service?.ServiceID,
    typeof service?.id === 'string' ? service.id : undefined,
    service?.__component,
].map(normalizeServiceId).filter(Boolean)

const getOrderUnitId = (order: any): number | undefined => {
    const unit = order?.Unit
    const value = order?.UnitID ?? order?.UnitId ?? order?.unitId ?? unit?.data?.id ?? unit?.id ?? unit
    const id = typeof value === 'number' ? value : Number(asText(value))
    return Number.isFinite(id) && id > 0 ? id : undefined
}

export const isConsultationOrder = (order: any): boolean => {
    const productId = order?.ProductID ?? order?.ProductId ?? order?.productID ?? order?.orderID ?? order?.OrderID
    if (Number(productId) === CONSULTATION_PRODUCT_ID) return true
    if (normalizeServiceId(order?.ServiceRef) === CONSULTATION_SERVICE_ID) return true
    return toArray<any>(order?.Service).some((service) => getServiceTokens(service).includes(CONSULTATION_SERVICE_ID))
}

export const getConsultationOrdersForUnit = (orders: any[], unitId: number | string | undefined): any[] => {
    const currentUnitId = Number(unitId)
    if (!Number.isFinite(currentUnitId) || currentUnitId <= 0) return []
    return toArray<any>(orders).filter((order) => isConsultationOrder(order) && getOrderUnitId(order) === currentUnitId)
}

export const resolveConsultationDisplay = (order: any): ConsultationDisplay => {
    const metadata = order?.ProductMetadata?.servicesMetadata ?? {}
    const fallbackPayload = metadata?.contract?.servicePayload ?? {}
    const rawContext = asText(metadata?.consultationContext) || asText(fallbackPayload?.consultationContext)
    const context = rawContext.toLowerCase()
    const label = CONTEXT_LABELS[context]

    return {
        title: label != null ? `Consultation Request - ${label}` : rawContext ? `Consultation Request - ${rawContext}` : 'Consultation Request',
        ...(rawContext ? { context: rawContext } : {}),
        ...(asText(metadata?.Comments) ? { comments: asText(metadata.Comments) } : {}),
    }
}
